import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatQty } from '../lib/quantity';
import { ordersApi, shipmentsApi, type SalesOrderDetail, type ShipmentDetail } from '../services/orders';
import { settingsApi, type BusinessPrintSettings } from '../services/settings';

function formatDateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

function getSnapshotValue(snapshot: Record<string, unknown> | null, key: string): string {
  const value = snapshot?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

function pickCompanyLine(value: string | null | undefined): string {
  return (value ?? '').trim();
}

export function ShipmentFoljesedelPage({ printMode = false }: { printMode?: boolean }) {
  const params = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { canRead } = useAuth();
  const orderId = Number(params.orderId);
  const shipmentId = Number(params.shipmentId);

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<SalesOrderDetail | null>(null);
  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [company, setCompany] = useState<BusinessPrintSettings | null>(null);

  if (!canRead('orders')) {
    return <Navigate to="/" replace />;
  }
  if (!Number.isFinite(orderId) || orderId <= 0 || !Number.isFinite(shipmentId) || shipmentId <= 0) {
    return <Navigate to="/orders" replace />;
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([ordersApi.get(orderId), shipmentsApi.get(shipmentId), settingsApi.getBusinessPrintSettings()])
      .then(([orderData, shipmentData, companyData]) => {
        if (cancelled) return;
        setOrder(orderData);
        setShipment(shipmentData);
        setCompany(companyData);
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(error instanceof Error ? error.message : 'Kunde inte ladda följesedel');
        navigate(`/orders/${orderId}`);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, orderId, shipmentId, toast]);

  const companyLogoUrl = useMemo(() => {
    const version = company?.logoMediaId ?? 0;
    return `${settingsApi.getCompanyLogoUrl()}?v=${version}`;
  }, [company?.logoMediaId]);

  const supplierLines = useMemo(() => {
    if (!company) return [];
    const lines = [
      pickCompanyLine(company.businessName ?? company.contactName),
      pickCompanyLine(company.address1 ?? company.contactAddress),
      [pickCompanyLine(company.postalCode), pickCompanyLine(company.city)].filter(Boolean).join(' '),
    ].filter(Boolean);
    return lines;
  }, [company]);

  const buyerLines = useMemo(() => {
    const snapshot = order?.customerSnapshotJson ?? null;
    const name = getSnapshotValue(snapshot, 'name') || shipment?.customerName || '';
    const email = getSnapshotValue(snapshot, 'email');
    const address1 = getSnapshotValue(snapshot, 'deliveryAddress1');
    const address2 = getSnapshotValue(snapshot, 'deliveryAddress2');
    const postalCity = [getSnapshotValue(snapshot, 'deliveryPostalCode'), getSnapshotValue(snapshot, 'deliveryCity')].filter(Boolean).join(' ');
    const country = getSnapshotValue(snapshot, 'deliveryCountry');
    return [name, address1, address2, postalCity, country, email].filter(Boolean);
  }, [order?.customerSnapshotJson, shipment?.customerName]);

  const rows = useMemo(() => shipment?.rows ?? [], [shipment?.rows]);
  const totalQty = rows.reduce((sum, row) => sum + Number(row.qtyShipped || 0), 0);
  const notes = (shipment?.notes ?? '').trim();

  if (loading || !order || !shipment || !company) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className={printMode ? 'min-h-screen bg-white p-0' : 'min-h-screen bg-neutral-200 p-3 print:bg-white print:p-0'}>
      <div className={printMode ? 'mx-auto flex min-h-screen max-w-none flex-col bg-white p-0 shadow-none' : 'mx-auto flex min-h-[calc(100vh-1.5rem)] max-w-[820px] flex-col bg-white p-6 shadow-lg print:mx-0 print:min-h-0 print:max-w-none print:p-0 print:shadow-none'}>
        {!printMode && (
          <div className="flex items-start justify-between gap-3 print:hidden">
          <Link
            to={`/orders/${orderId}`}
            className="inline-flex rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-100"
          >
            Tillbaka
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded bg-success px-3 py-1.5 text-sm text-white hover:bg-success-hover"
          >
            Skriv ut
          </button>
        </div>
        )}

        <div className="flex-1">
          <header className={printMode ? 'grid grid-cols-[1fr_auto_1fr] items-start gap-3 px-4 py-6' : 'grid grid-cols-[1fr_auto_1fr] items-start gap-3 px-4 py-6 print:py-0'}>
            <div className="flex items-start">
              <img
                src={companyLogoUrl}
                alt="Företagslogga"
                className="max-h-16 w-auto object-contain"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            </div>
            <div className="pt-3 text-center">
              <h1 className="text-[24px] font-semibold tracking-[-0.03em] text-black">
                Följesedel
              </h1>
            </div>
            <div className="flex items-start justify-end">
              <img src="/logga.png" alt="ApisAI" className="h-10 max-w-[120px] w-auto object-contain" />
            </div>
          </header>

          <section className={printMode ? 'grid grid-cols-2 gap-6 pt-4' : 'grid grid-cols-1 gap-6 pt-4 md:grid-cols-2 print:grid-cols-2 print:pt-4'}>
            <div className="space-y-1 text-sm">
              <div className="font-semibold">Leverantör</div>
              {supplierLines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
            <div className="space-y-1 text-sm md:justify-self-start">
              <div className="font-semibold">Köpare</div>
              {buyerLines.length > 0 ? buyerLines.map((line) => <div key={line}>{line}</div>) : <div>-</div>}
            </div>
          </section>

          <div className="pt-6 text-sm">
            Leveransdatum: {formatDateOnly(shipment.shipDate)}
          </div>

          <div className="pt-3">
            <div className="overflow-hidden border border-black">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#0b8a8b] text-white">
                    <th className="w-28 px-2 py-2 text-left font-semibold">Batchnummer</th>
                    <th className="px-2 py-2 text-left font-semibold">Produkt</th>
                    <th className="px-2 py-2 text-right font-semibold">Antal</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const batchCode = row.batchCode || row.containerCode || '-';
                    return (
                      <tr key={row.id} className="border-t border-black">
                        <td className="w-28 px-2 py-2 text-left align-top whitespace-nowrap">{batchCode}</td>
                        <td className="px-2 py-2 align-top">{row.productName ?? '-'}</td>
                        <td className="px-2 py-2 text-right align-top">
                          {formatQty(row.qtyShipped, row.uomLabel)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t border-black">
                    <td colSpan={2} className="px-2 py-2 text-right font-semibold">
                      Totalt
                    </td>
                    <td className="px-2 py-2 text-right font-semibold">
                      {formatQty(totalQty, rows[0]?.uomLabel)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {notes !== '' && (
            <section className="pt-10">
              <div className="mb-1 text-sm font-semibold">Anteckningar</div>
              <div className="min-h-28 rounded border border-black p-3 text-sm whitespace-pre-wrap">{notes}</div>
            </section>
          )}
        </div>

        <footer className="mt-auto pt-16 text-[11px] leading-tight">
          <div className={printMode ? 'grid grid-cols-4 gap-4' : 'grid grid-cols-1 gap-4 md:grid-cols-4 print:grid-cols-4'}>
            <div className="space-y-0.5">
              <div className="font-semibold">Adress</div>
              {[
                pickCompanyLine(company.businessName ?? company.contactName),
                pickCompanyLine(company.address1 ?? company.contactAddress),
                [pickCompanyLine(company.postalCode), pickCompanyLine(company.city)].filter(Boolean).join(' '),
                pickCompanyLine(company.country),
              ]
                .filter(Boolean)
                .map((line) => (
                  <div key={line}>{line}</div>
                ))}
            </div>
            <div className="space-y-0.5">
              <div className="font-semibold">Telefon</div>
              <div>{company.phone ?? ''}</div>
              <div className="font-semibold pt-1">E-post</div>
              <div>{company.email ?? ''}</div>
              <div className="font-semibold pt-1">Webbadress</div>
              <div>{company.web ?? ''}</div>
            </div>
            <div className="space-y-0.5">
              <div className="font-semibold">Bankgiro</div>
              <div>{company.bankgiro ?? ''}</div>
              <div className="font-semibold pt-1">Organisationsnr</div>
              <div>{company.orgNr ?? ''}</div>
            </div>
            <div className="space-y-0.5">
              <div className="font-semibold">Momsreg.nr</div>
              <div>{company.vatNr ?? ''}</div>
              <div className="font-semibold pt-1">Säte</div>
              <div>{company.seat ?? ''}</div>
              {company.hasFSkatt && <div className="pt-1 font-semibold">Godkänd för F-skatt</div>}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
